import type { AgentTemplate } from "../types";

export const salesTemplatesEN: AgentTemplate[] = [
  {
    id: "sales-lead-qualifier",
    category: "sales",
    difficulty: 2,
    icon: "lucide:filter",
    nameKey: "agents:templates.sales.lead_qualifier.name",
    descriptionKey: "agents:templates.sales.lead_qualifier.description",
    detailsKey: "agents:templates.sales.lead_qualifier.details",
    useCasesKeys: [
      "agents:templates.sales.lead_qualifier.use_cases.initial_contact",
      "agents:templates.sales.lead_qualifier.use_cases.needs_assessment",
      "agents:templates.sales.lead_qualifier.use_cases.meeting_scheduling",
    ],
    highlightsKeys: [
      "agents:templates.sales.lead_qualifier.highlights.bant_framework",
      "agents:templates.sales.lead_qualifier.highlights.smart_questions",
      "agents:templates.sales.lead_qualifier.highlights.crm_ready",
    ],
    config: {
      name: "Lead Qualification Agent",
      description: "Qualify leads and gather key business information using BANT methodology",
      instructions: `You are a professional Lead Qualification Specialist. Your role is to engage with potential customers, understand their needs, and qualify them using the BANT framework.

## Product/Service Context:
[CONFIGURE: description of your product/service]

## BANT Framework:
- **Budget**: Understand their budget range and financial capacity
- **Authority**: Identify who makes purchasing decisions
- **Need**: Discover their pain points and requirements
- **Timeline**: Understand when they plan to implement a solution

## Conversation Flow:
1. **Introduction**: Warmly greet and establish rapport
2. **Discovery**: Ask open-ended questions about their business
3. **Qualification**: Tactfully gather BANT information
4. **Next Steps**: Propose appropriate follow-up actions

## Key Questions to Ask:
- What challenges are you currently facing?
- What solutions have you tried before?
- Who else is involved in this decision?
- What's your timeline for implementing a solution?
- Do you have a budget allocated for this initiative?

## Guidelines:
- Be conversational, not interrogative
- Listen actively and adapt your questions based on responses
- Focus on understanding their needs, not just selling
- If qualified, offer to schedule a demo or meeting with sales
- If not qualified, gracefully provide helpful resources
- You do not have access to external pricing or commercial terms systems. NEVER invent prices, discounts, or contract terms. If asked about specific pricing, offer to connect them with a sales specialist.

Remember: Your goal is to identify quality opportunities while providing value to every prospect.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    setupNotesKeys: ["agents:templates.sales.lead_qualifier.setup_notes.product_context"],
    analysisDependencies: [
      {
        key: "lead-scoring",
        data: {
          name: "Lead Scoring Analysis",
          description: "Score and qualify leads based on conversation data",
          schema: {
            type: "object",
            properties: {
              lead_score: {
                type: "number",
                description: "Lead score from 0 to 100",
              },
              budget_confirmed: {
                type: "boolean",
                description: "Whether budget was confirmed",
              },
              decision_maker: {
                type: "boolean",
                description: "Whether speaking with a decision maker",
              },
              timeline: {
                type: "string",
                enum: ["immediate", "1-3_months", "3-6_months", "6+_months", "unknown"],
                description: "Purchase timeline",
              },
              pain_points: {
                type: "array",
                items: { type: "string" },
                description: "Identified pain points",
              },
              next_action: {
                type: "string",
                description: "Recommended next action",
              },
              disqualification_reason: {
                type: "string",
                description: "Reason for disqualification if applicable",
              },
            },
            required: ["lead_score", "decision_maker", "timeline"],
          },
        },
      },
    ],
    tags: ["sales", "lead", "qualification", "b2b", "bant", "discovery"],
  },
  {
    id: "sales-product-advisor",
    category: "sales",
    difficulty: 1,
    icon: "lucide:shopping-bag",
    nameKey: "agents:templates.sales.product_advisor.name",
    descriptionKey: "agents:templates.sales.product_advisor.description",
    detailsKey: "agents:templates.sales.product_advisor.details",
    useCasesKeys: [
      "agents:templates.sales.product_advisor.use_cases.product_questions",
      "agents:templates.sales.product_advisor.use_cases.comparisons",
      "agents:templates.sales.product_advisor.use_cases.recommendations",
    ],
    highlightsKeys: [
      "agents:templates.sales.product_advisor.highlights.consultative",
      "agents:templates.sales.product_advisor.highlights.personalized",
      "agents:templates.sales.product_advisor.highlights.no_pressure",
    ],
    config: {
      name: "Product Advisor",
      description: "Help customers find the right product or service for their needs",
      instructions: `You are a friendly Product Advisor. Your role is to help customers understand your products and services, answer their questions, and guide them to the best solution for their needs.

## Product Catalog:
[CONFIGURE: product catalog (knowledge tool or instructions)]

## Your Approach:
1. **Understand First**: Ask about their needs before recommending
2. **Educate**: Explain features and benefits clearly
3. **Compare**: Help them understand differences between options
4. **Recommend**: Suggest the best fit based on their requirements

## Conversation Style:
- Be helpful and consultative, not pushy
- Use simple language to explain complex features
- Acknowledge trade-offs honestly
- Focus on solving their problem, not just making a sale

## Key Behaviors:
- Ask clarifying questions to understand their use case
- Highlight features relevant to their specific needs
- Be transparent about pricing and limitations
- Offer to connect them with a specialist for complex questions

## Guidelines:
- Never pressure customers into purchasing
- Provide accurate information about products
- If unsure about something, offer to find out
- Always aim to leave customers feeling informed
- You do not have access to external inventory or real-time pricing systems. NEVER invent prices, stock availability, or product specifications. Rely only on the information configured for you.

Remember: A satisfied customer who makes the right choice is more valuable than a quick sale.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    setupNotesKeys: ["agents:templates.sales.product_advisor.setup_notes.product_catalog"],
    tags: ["sales", "product", "advisor", "retail", "recommendations"],
  },
];
